/**
 * FormSection Component
 *
 * Labeled section within a form:
 * - Section label with optional icon
 * - Optional description text
 * - Content container with consistent spacing
 */

import { ReactNode } from 'react';
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  icon: {
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  label: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  description: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    paddingLeft: '0',
  },
});

export interface FormSectionProps {
  /** Section label */
  label: string;
  /** Optional icon displayed before the label */
  icon?: ReactNode;
  /** Optional description text below the label */
  description?: string;
  /** Section content */
  children: ReactNode;
  /** Test ID for the component */
  testId?: string;
}

export function FormSection({
  label,
  icon,
  description,
  children,
  testId,
}: FormSectionProps) {
  const styles = useStyles();

  return (
    <div className={styles.section} data-testid={testId}>
      <div className={styles.header}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          {description && <Text className={styles.description}>{description}</Text>}
        </div>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
}
